import React, { createContext, useState, useEffect } from 'react';
import axios from 'axios';
import apiService from '../services/apiService';
import FlashMessage from './FlashMessage';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config';
import { errorLinesFrom } from '../utils/apiError';

export const AuthContext = createContext();

// Pages a logged-out visitor is entitled to be on. A dead token found in localStorage
// must not throw someone off Signup — that was half of the "signup bounces home" bug.
const PUBLIC_ROUTES = ['/', '/login', '/signup'];

const onPublicRoute = () =>
  typeof window !== 'undefined' && PUBLIC_ROUTES.includes(window.location.pathname);

const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [flashMessage, setFlashMessage] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [userId, setUserId] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    console.log("AuthContext State:", { isAuthenticated, user, token, userId });
  }, [isAuthenticated, user, token, userId]);

  // Every login path (password or Google) lands here, so session state is identical
  // regardless of how the user signed in.
  const applySession = (data) => {
    const { access_token, refresh_token, account_id } = data;

    localStorage.setItem('token', access_token);
    localStorage.setItem('account_id', account_id);
    if (refresh_token) {
      localStorage.setItem('refresh_token', refresh_token);
    }

    setToken(access_token);
    setIsAuthenticated(true);
    setUser(data.account_object);
    setUserId(account_id);
  };

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API_URL}/accounts/login`, {
        username,
        password,
      });
      applySession(response.data);
    } catch (err) {
      console.error("Login failed:", err);
      // Prefer what the server said over a canned message, but keep a sensible
      // fallback for the case where the request never got a response at all.
      throw new Error(errorLinesFrom(err, "Invalid login credentials").join(' '));
    }
  };

  const loginWithGoogle = async (idToken) => {
    try {
      const data = await apiService.googleLogin(idToken);
      applySession(data);
      await checkAuth();
    } catch (err) {
      console.error("Google login failed:", err);
      // `detail` is a string here (401 "Invalid Google token.", 503 when no client ID
      // is configured) but goes through the same unpacker so a 422 array can't reach
      // the page as "[object Object]".
      throw new Error(errorLinesFrom(err, "Google sign-in failed").join(' '));
    }
  };

  // Drop the session locally, without telling the API or moving the user. Used both by
  // the deliberate logout below and by checkAuth when the stored token turns out to be
  // dead, which are the same cleanup but very different things to do to the user.
  const clearLocalSession = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('account_id');
    localStorage.removeItem('refresh_token');
    setIsAuthenticated(false);
    setUser(null);
    setUserId(null);
    setToken(null);
  };

  const logout = async () => {
    try {
      const response = await apiService.logout();
      setFlashMessage(response.message || "Logged out successfully.");
    } catch (err) {
      console.error("Error during logout:", err);
      setFlashMessage("Error during logout. Please try again.");
    } finally {
      clearLocalSession();
      navigate('/login');
    }
  };

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await axios.get(`${API_URL}/accounts/protected`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setIsAuthenticated(true);
      setUser(response.data.account_object);
      setUserId(response.data.account_object.sub);
      setToken(token);

      const { subscription_expiration } = response.data.account_object;
      const isExpired = new Date(subscription_expiration) < new Date();
      if (isExpired) {
        console.warn("Subscription expired. Limited access provided.");
        setFlashMessage("Your subscription has expired. Please renew to regain full access.");
      }
    } catch (err) {
      console.error("Authentication failed:", err);
      setIsAuthenticated(false);

      if (err.response?.status === 401) {
        // The stored token is dead. Bin it — but calling logout() here would POST
        // /accounts/logout with that same dead token (another guaranteed 401) and then
        // march the user to /login even if they were only trying to sign up.
        clearLocalSession();

        if (!onPublicRoute()) {
          setFlashMessage("Session expired. Please log in again.");
          navigate('/login');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, token, login, loginWithGoogle, logout, loading }}>
      {children}
      {flashMessage && <FlashMessage message={flashMessage} onClose={() => setFlashMessage(null)} />}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
